import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { PokemonCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useI18n } from '../../contexts/I18nContext';

interface AddableCardItemProps {
  card: PokemonCard;
  quantity: number;
  onUpdateQuantity: (quantity: number) => void;
  maxQuantity?: number;
  disabled?: boolean;
}

export function AddableCardItem({
  card,
  quantity = 0,
  onUpdateQuantity,
  maxQuantity = 2,
  disabled = false
}: AddableCardItemProps) {
  const { lang } = useI18n();
  const cardName = lang === 'en' ? card.card_en || card.card_fr : card.card_fr || card.card_en;
  
  // Pour l'anglais, placer "en/" avant "preview/"
  const imagePath = card.image 
    ? `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/preview/' : 'preview/'}${card.image}` 
    : null;

  const handleQuantityChange = (e: React.MouseEvent, delta: number) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuantity = quantity + delta;
    if (newQuantity >= 0 && newQuantity <= maxQuantity) {
      onUpdateQuantity(newQuantity);
    }
  };

  return (
    <div className={`group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${disabled ? 'opacity-50' : ''}`}>
      <div className="aspect-[3/4] overflow-hidden py-2 md:py-4">
        {imagePath && (
          <img
            src={imagePath}
            alt={cardName || 'Pokémon Card'}
            className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-300"
          />
        )}
      </div>
      
      <div className="p-2 md:p-4">
        <h3 className="text-sm md:text-lg font-semibold text-gray-900 truncate">
          {cardName}
        </h3>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs md:text-sm text-gray-500 truncate">{card.set}</span>
          <span className="px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
            {card.rarity}
          </span>
        </div>
      </div>

      <div className="absolute top-1 md:top-2 right-1 md:right-2 flex items-center gap-0.5 md:gap-1 bg-white/90 backdrop-blur-sm rounded-lg p-0.5 md:p-1 shadow-lg">
        <button
          onClick={(e) => handleQuantityChange(e, -1)}
          disabled={quantity === 0 || disabled}
          className="p-0.5 md:p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Minus className="w-3 h-3 md:w-4 md:h-4" />
        </button>
        <span className="w-6 md:w-8 text-center text-sm md:text-base font-medium">{quantity}</span>
        <button
          onClick={(e) => handleQuantityChange(e, 1)}
          disabled={quantity >= maxQuantity || disabled}
          className="p-0.5 md:p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3 h-3 md:w-4 md:h-4" />
        </button>
      </div>
    </div>
  );
}